export type SystemType = 'rigid' | 'elastic12' | 'nonlinear' | 'sculpture' | 'resonantPendulum';

export interface Vec4 {
  x: number; y: number; z: number; w: number;
}

export interface PendulumParams {
  m1: number;
  m2: number;
  L1: number;
  L2: number;
  k1: number;
  k2: number;
  sculptureWeight: number;
  sculptureRod: number;
  sculptureAxle: number;
  sculptureReduction: number;
  sculptureN: number;
  rpM0: number;
  rpM1: number;
  rpL0: number;
  rpL1: number;
  rpA0: number;
}

export interface SimSettings {
  dt: number;
  stepsPerFrame: number;
}

export type PhaseSpaceDimension =
  | 'angle1' | 'velocity1' | 'angle2' | 'velocity2'
  | 'stretch1' | 'stretchRate1' | 'stretch2' | 'stretchRate2';

export const DIM_ORDER: PhaseSpaceDimension[] = [
  'angle1', 'velocity1', 'stretch1', 'stretchRate1',
  'angle2', 'velocity2', 'stretch2', 'stretchRate2',
];

export function systemDimensions(system: SystemType, sculptureN: number): PhaseSpaceDimension[] {
  if (system === 'sculpture') {
    const count = 2 * Math.max(1, Math.min(4, Math.floor(sculptureN)));
    return DIM_ORDER.slice(0, count);
  }
  if (system === 'rigid') return ['angle1', 'velocity1', 'angle2', 'velocity2'];
  if (system === 'resonantPendulum') return ['angle1', 'velocity1', 'angle2', 'velocity2'];
  return DIM_ORDER.slice(0, 8);
}

export interface SimulationConfig {
  system: SystemType;
  params: PendulumParams;
  sim: SimSettings;
  initialState: Record<PhaseSpaceDimension, number>;
}

export const SYSTEM_NAMES: Record<SystemType, string> = {
  rigid: 'Rigid',
  elastic12: 'Elastic Double Pendulum',
  nonlinear: 'Nonlinear Elastic Pendulum',
  sculpture: 'Kinetic Sculpture',
  resonantPendulum: 'Resonant Pendulum',
};

export const DEFAULT_PARAMS: PendulumParams = {
  m1: 1, m2: 1, L1: 1, L2: 1, k1: 50, k2: 50,
  sculptureWeight: 1000, sculptureRod: 50, sculptureAxle: 10,
  sculptureReduction: 0.1, sculptureN: 3,
  rpM0: 1000, rpM1: 100, rpL0: 50, rpL1: 10, rpA0: 10,
};

export const DEFAULT_SIM: SimSettings = {
  dt: 0.002,
  stepsPerFrame: 15,
};

export const DEFAULT_INITIAL_STATE: Record<PhaseSpaceDimension, number> = {
  angle1: 0, velocity1: 0, angle2: 0, velocity2: 0,
  stretch1: 0, stretchRate1: 0, stretch2: 0, stretchRate2: 0,
};
